import { api } from "@/lib/api/client"
import type { BaseResponse, IdOnly } from "@/types/api"
import type { Contract } from "@/types/domain"

export interface ContractWriteRequest {
  contractNumber?: string | null
  contractDate?: string | null
  contractType?: string | null
  clientName?: string | null
  productType?: string | null
  cocoaGrade?: string | null
  quantity?: number | null
  origin?: string | null
  basePrice?: number | null
  currency?: string | null
  paymentTerms?: string | null
  incoterms?: string | null
  certificates?: string[] | null
  notes?: string | null
  adjustments?: unknown
  portOfLoading?: string | null
  destinationPort?: string | null
  differential?: number | null
  countryDifferential?: number | null
  pricingMechanism?: string | null
  referenceMarket?: string | null
  calculationMethod?: string | null
  floatingDiscount?: number | null
  fixingStartDate?: string | null
  fixingEndDate?: string | null
  referenceIndex?: string | null
  referenceMonth?: string | null
  referenceYear?: number | null
  contractDurationStart?: string | null
  contractDurationEnd?: string | null
  agroforestryBonusPerKilo?: number | null
  certificationPremiumPerKilo?: number | null
  fairtradePremiumPerKilo?: number | null
  localAgroforestryBonusPerKilo?: number | null
  localCertificationPremiumPerKilo?: number | null
  organicPremiumPerKilo?: number | null
  rainforestPremiumPerKilo?: number | null
  utzPremiumPerKilo?: number | null
  onlyAcceptsEUDR?: boolean | null
  onlyAcceptsCertificates?: string[] | null
  onlyAcceptNoChildLabor?: boolean | null
}

export async function listContracts(): Promise<Contract[]> {
  const { data } = await api.get<BaseResponse<Contract[]>>("/exporter/contracts")
  if (!data.success) {
    throw new Error(data.message ?? "Could not load contracts")
  }
  return data.result ?? []
}

export async function getContract(contractId: string): Promise<Contract> {
  const { data } = await api.get<BaseResponse<Contract>>(
    `/exporter/contracts/${contractId}`,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not load contract")
  }
  return data.result
}

export async function createContract(payload: ContractWriteRequest): Promise<IdOnly> {
  const { data } = await api.post<BaseResponse<IdOnly>>(
    "/exporter/contracts",
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not create contract")
  }
  return data.result
}

export async function updateContract(
  contractId: string,
  payload: ContractWriteRequest,
): Promise<IdOnly> {
  const { data } = await api.put<BaseResponse<IdOnly>>(
    `/exporter/contracts/${contractId}`,
    payload,
  )
  if (!data.success || !data.result) {
    throw new Error(data.message ?? "Could not update contract")
  }
  return data.result
}

export async function deleteContract(contractId: string): Promise<void> {
  const { data } = await api.delete<BaseResponse>(
    `/exporter/contracts/${contractId}`,
  )
  if (!data.success) {
    throw new Error(data.message ?? "Could not delete contract")
  }
}
